import type { ZoneAccess } from '../api/types'
import { toDutch } from '../domain/disclaimers'
import { describeZone, zoneAccessBasis, type ZoneTone } from '../domain/zones'
import { Icon, type IconName } from './Icon'

/**
 * Where this vehicle is still allowed to drive.
 *
 * The only card in the report about what somebody may *do* with the vehicle rather than what it is
 * — and therefore the only one that can send a reader somewhere they are not allowed. That shapes
 * everything about it.
 *
 * **The five verdicts are five verdicts.** "Geldt niet voor dit voertuig" is not a permission: a
 * passenger car is not *allowed* into a zero-emission zone, the zone is simply not written about
 * it. The API keeps `NotApplicable` and `Allowed` apart on purpose, and drawing them the same way
 * would eventually tell a van driver something that is only true of cars. Likewise "niet te
 * bepalen" is not "geen toegang": the RDW publishes no emission class for plenty of older
 * vehicles, and turning that silence into a refusal would be inventing a rule.
 *
 * **`allowedUntil` is the reason the section exists.** A diesel van that keeps its access until a
 * published date is worth a great deal more today than it will be the morning after, and that date
 * is the whole answer — so it goes into the verdict itself rather than into a tick with a footnote.
 *
 * **It says whether the vehicle qualifies, not where the zones are.** Which municipality operates
 * which zone changes on its own schedule and is published by the municipalities; the hard half, and
 * the half worth answering, is whether this vehicle meets a given zone's requirement. Hence the
 * link out.
 */
export function ZoneAccessSection({ zoneAccess }: { zoneAccess: ZoneAccess }) {
  const zones = zoneAccess.zones.map(describeZone)
  const disclaimers = toDutch(zoneAccess.disclaimers)

  return (
    <div className="zones">
      <ul className="zones__basis">
        {zoneAccessBasis(zoneAccess).map((fact) => (
          <li className="zones__chip" key={fact}>
            {fact}
          </li>
        ))}
      </ul>

      <ul className="zones__list">
        {zones.map((zone) => (
          <li className={`zones__item zones__item--${zone.tone}`} key={zone.zone}>
            <span className="zones__icon">
              <Icon name={ICONS[zone.tone]} size={18} />
            </span>

            <span className="zones__name">
              {zone.title}
              <span className="zones__applies">{zone.appliesTo}</span>
            </span>

            <span className="zones__verdict">
              {zone.verdict}
              {zone.detail && <span className="zones__detail">{zone.detail}</span>}
            </span>
          </li>
        ))}
      </ul>

      <p className="zones__more">
        <a href={zoneAccess.moreInformationUrl} target="_blank" rel="noreferrer noopener">
          Bekijk welke gemeenten welke zone hebben
        </a>
      </p>

      {disclaimers.length > 0 && (
        <ul className="card__footnote zones__disclaimers">
          {disclaimers.map((disclaimer) => (
            <li key={disclaimer}>{disclaimer}</li>
          ))}
        </ul>
      )}
    </div>
  )
}

/**
 * A glyph per tone rather than per zone.
 *
 * The zones are told apart by name; what a reader has to take in at a glance is the verdict, so the
 * icon carries that instead. The muted "info" for a zone that does not apply is the one that
 * matters — a tick there would read as a pass.
 */
const ICONS: Record<ZoneTone, IconName> = {
  positive: 'check',
  warning: 'calendar',
  negative: 'warning',
  muted: 'info',
  neutral: 'info',
}
